import { Injectable } from '@angular/core';
import { App } from '@df/core/app';
import get from 'lodash-es/get';
import { Subject } from 'rxjs';
import { ICmsModel } from './../../resource/model/icms-model';

export namespace NICmsDataChange {
    export enum EChangeType {
        delete,
        add,
        change,
        swap,
        move
    }

    export interface IChangeDataEvent<TObject = any> {
        changeType: EChangeType;
        model: ICmsModel;
        valuePath: TValuePath;
        changedObject: TObject;
        flag?: TChangeFlag;
        bulk?: {
            first: boolean;
            last: boolean;
        };
    }

    export type TValuePath = (string | number)[];

    export type TChangeFlag = 'dataSwapOnSize' | 'dataInit' | 'dataReset' | 'empty';

    export interface IEventDelete<TObject = any, TValue = any> extends IChangeDataEvent<TObject> {
        changeType: EChangeType.delete;
        previousValue: TValue; // deleted value
    }
    export interface IEventAdd<TObject = any, TValue = any> extends IChangeDataEvent<TObject> {
        changeType: EChangeType.add;
        value: TValue; // value to be added
    }

    export interface IEventChange<TObject = any, TValue = any> extends IChangeDataEvent<TObject> {
        model: ICmsModel;
        changeType: EChangeType.change;
        value: TValue;
        previousValue: TValue;
    }
    export interface IEventSwap<TObject = any, TValue = any> extends IChangeDataEvent<TObject> {
        changeType: EChangeType.swap;
        value: TValue;
        previousValuePath: TValuePath; // path in model that is changed
    }
    export interface IEventMove<TObject = any, TValue = any> extends IChangeDataEvent<TObject> {
        changeType: EChangeType.move;
        value: TValue;
        previousValuePath: TValuePath; // path in model that is changed
    }
}

declare module '@df/core/app' {
    interface App {
        iCmsDataChangeService: ICmsDataChangeService;
    }
}

/**
 * Service reporting any changes
 */
@Injectable({ providedIn: 'root' })
export class ICmsDataChangeService {
    /**
     * Objet to observe any cms data changes before it is applied
     */
    readonly beforeChange$ = new Subject<NICmsDataChange.IChangeDataEvent>();

    /**
     * Objet to observe any cms data changes after it is applied
     */
    readonly afterChange$ = new Subject<NICmsDataChange.IChangeDataEvent>();

    constructor(app: App) {
        app.iCmsDataChangeService = this;
    }

    notify(event: NICmsDataChange.IChangeDataEvent, applyChange: () => void) {
        this.beforeChange$.next(event);
        applyChange();
        this.afterChange$.next(event);
    }

    isDataInit(event: NICmsDataChange.IChangeDataEvent): boolean {
        return (
            event.changeType === NICmsDataChange.EChangeType.change &&
            event.valuePath.length === 1 &&
            event.valuePath[0] === 'data' &&
            !(event as NICmsDataChange.IEventChange).previousValue
        );
    }

    filterByData(event: NICmsDataChange.IChangeDataEvent, modelType: string, paths: NICmsDataChange.TValuePath[]) {
        if (event.model.type !== modelType) {
            return false;
        }

        const changedPath = event.valuePath;

        return paths.find(path => this.pathContains(path, changedPath));
    }

    retrieveValue(event: NICmsDataChange.IEventChange, path: NICmsDataChange.TValuePath) {
        return this.retrieveSubValue(event.value, event.valuePath, path);
    }

    retrievePreviousValue(event: NICmsDataChange.IEventChange | NICmsDataChange.IEventDelete, path: NICmsDataChange.TValuePath) {
        return this.retrieveSubValue(event.previousValue, event.valuePath, path);
    }

    /**
     * This method may not work best since we need to refer to data that might have been overwritten
     * by things comming in back from BE
     * @param event
     */
    syncEventData(event: NICmsDataChange.IChangeDataEvent) {
        const path = event.valuePath.slice(0, -1);
        event.changedObject = path.length ? get(event.model, path) : event.model;
    }

    private retrieveSubValue(value, valuePath: NICmsDataChange.TValuePath, path: NICmsDataChange.TValuePath) {
        if (
            path.slice(valuePath.length).find(part => {
                if (typeof value === 'object') {
                    value = value[part];
                    return false;
                }
                return true;
            })
        ) {
            return undefined;
        }

        return value;
    }

    private pathContains(testedPath: NICmsDataChange.TValuePath, changedPath: NICmsDataChange.TValuePath): boolean {
        return !changedPath.find((part, index) => testedPath[index] !== part);
    }
}
